// SubProfile(代理グループ/サブメンバー)機能のドメイン型と stateful composable。
// グループはメイン1つにつき1つ(単数)。サブメンバー=最大10名、住所/メール/電話はメイン参照で持たない。
// 生成型(ProxyGroup)を正とする。custom-fetch が snake_case→camelCase 変換するため CamelCaseKeys を被せる。

// vitest からは Nuxt auto-import が効かないため明示 import に。
import { computed, readonly, ref, watchEffect, type Ref } from 'vue'
import { storage } from '~/utils/session-storage'
import {
  getGroup,
  createGroup,
  updateGroup,
  deleteGroup,
  addGroupMember,
  updateGroupMember,
  deleteGroupMember,
} from './api/group'
import type {
  GetProxyGroup200,
  PostProxyGroupSubMemberMutationRequest,
  PatchProxyGroupSubMemberMutationRequest,
} from '~/types/api/ProxyGroup'
import type { SubMember } from '~/types/api/_atoms/SubMember'
import type { SubMemberSex } from '~/types/api/_atoms/SubMemberSex'
import type { CamelCaseKeys } from '~/types/util'

export const GROUP_NAME_MAX_LENGTH = 10
export const GROUP_MEMBER_MAX_COUNT = 10

/**
 * サブメンバー。氏名は姓<空白>名で結合。id は proxy_group_member_id に一致。
 * sex/birthday は生成型では必須だが、UI/PRD 上は任意のため nullable に上書きする。
 */
export type GroupMember = Omit<CamelCaseKeys<SubMember>, 'sex' | 'birthday'> & {
  sex: SubMemberSex | null
  birthday: string | null
}
/**
 * 代理グループ(メイン1つにつき1つ)。{ id, name, status, subMembers }。
 * subMembers は nullable 上書き済みの GroupMember[] に揃え、Group 経由で読む箇所にも
 * sex/birthday の任意性(null 可)を型で伝える。
 */
export type Group = Omit<CamelCaseKeys<GetProxyGroup200>, 'subMembers'> & {
  subMembers: GroupMember[]
}

// リクエストボディ型(camelCase。custom-fetch が送信時に snake_case へ変換)。
export interface GroupNameInput {
  name: string
}
export type CreateGroupRequest = GroupNameInput
export type UpdateGroupRequest = GroupNameInput

/**
 * サブメンバーのフォーム prefill 値(編集時の初期値)。既存メンバーは sex/birthday が
 * 未登録のレガシーデータで null になり得るため null 許容。氏名・ふりがなは姓<空白>名で結合。
 * 送信ボディは下記 Create/UpdateMemberRequest(生成型)を正とする。
 */
export interface MemberInput {
  name: string
  nameRuby: string
  birthday: string | null
  sex: SubMemberSex | null
}
// 送信ボディは生成型を正にする。POST=birthday 必須/sex 任意、PATCH=全項目任意(部分更新)。
export type CreateMemberRequest = CamelCaseKeys<PostProxyGroupSubMemberMutationRequest>
export type UpdateMemberRequest = CamelCaseKeys<PatchProxyGroupSubMemberMutationRequest>

// group を module-level + session-storage で保持(useMe/useRelatedStore と同方針)。
// グループ作成はグループ名入力の確定時に即 POST するため、ページ跨ぎの入力バッファは持たない。
//
// storage.getItem は未保存時に {} を返し null 既定を無視する(session-storage.ts)。
// 未作成を表す null と {} を取り違えないよう、空オブジェクトは null に正規化する。
// あわせて旧 shape(subMembers を持たない移行前 group)が残存した場合も新 shape として
// 安全に扱えないため破棄する(me.ts get() の構造ガードと同方針)。
const restored = (value: Group | null): Group | null =>
  value && Array.isArray(value.subMembers) ? value : null

const groupStorage = storage<Group | null>('group')
const group = ref<Group | null>(restored(groupStorage.getItem(null)))
watchEffect(() => groupStorage.setItem(group.value))

const memberCount = computed(() => group.value?.subMembers.length ?? 0)

/** 常に最新を取得して state を更新(未作成なら null)。 */
// サブメンバーを proxy_group_member_id(=GroupMember.id) の昇順=作成順で保持する。
// バックエンドが順序を保証しないため、グループ管理画面・getTargets・予約一覧などの表示順を
// 「作成順(pgm昇順)」で一貫させるよう、group state を持つこの層で一元的に並べ替える。
const sortSubMembers = (g: Group): Group => ({
  ...g,
  subMembers: [...g.subMembers].sort((a, b) => a.id - b.id),
})

const get = async (): Promise<Group | null> => {
  const fetched = await getGroup()
  group.value = fetched ? sortSubMembers(fetched) : null
  return group.value
}

const create = async (body: CreateGroupRequest): Promise<Group> => {
  group.value = sortSubMembers(await createGroup(body))
  return group.value
}

const update = async (body: UpdateGroupRequest): Promise<Group> => {
  group.value = sortSubMembers(await updateGroup(body))
  return group.value
}

const remove = async (): Promise<void> => {
  await deleteGroup()
  group.value = null
}

const addMember = async (body: CreateMemberRequest): Promise<GroupMember> => {
  const member = await addGroupMember(body)
  await get()
  return member
}

const updateMember = async (memberId: number, body: UpdateMemberRequest): Promise<GroupMember> => {
  const member = await updateGroupMember(memberId, body)
  await get()
  return member
}

const removeMember = async (memberId: number): Promise<void> => {
  await deleteGroupMember(memberId)
  await get()
}

/**
 * グループ state を破棄する(サインイン時に useAuth().get() から呼ばれる)。
 * サインアウトは clearSessionStorage が全 key を消すが、セッション失効後の
 * 再サインインはそこを通らないため、前ユーザーの state を引き継がないようここで担保する。
 */
export const resetGroupState = () => {
  group.value = null
}

export const useGroup = () => {
  return {
    group: readonly(group) as Readonly<Ref<Group | null>>,
    memberCount,
    get,
    create,
    update,
    remove,
    addMember,
    updateMember,
    removeMember,
  }
}
